import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Suites = () => {
  const [suites, setSuites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/suites`)
      .then((res) => res.json())
      .then((data) => {
        setSuites(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching suites:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div data-testid="suites-loading" className="min-h-screen flex items-center justify-center">
        <p className="text-[#3A3A3A]">Loading suites...</p>
      </div>
    );
  }

  return (
    <div data-testid="suites-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="suites-page-title"
          className="font-serif font-bold tracking-tighter leading-none text-[#1E3A8A] mb-6"
          style={{ fontSize: '26px' }}
        >
          Our Suites & Apartments
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="suites-page-subtitle"
          className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto"
        >
          Each suite is a masterpiece of design and comfort. Choose the perfect sanctuary for your stay.
        </motion.p>
      </section>

      {/* Suites Grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="space-y-16">
          {suites.map((suite, index) => (
            <motion.div
              key={suite.id}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid={`suite-detail-${index}`}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
            >
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative h-[500px] overflow-hidden">
                  <img
                    src={suite.image_url}
                    alt={suite.name}
                    data-testid={`suite-detail-image-${index}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h2
                  data-testid={`suite-detail-name-${index}`}
                  className="font-serif text-3xl sm:text-4xl tracking-tight font-light text-[#1A1A1A] mb-4"
                >
                  {suite.name}
                </h2>
                <p
                  data-testid={`suite-detail-description-${index}`}
                  className="text-base leading-relaxed text-[#3A3A3A] mb-6"
                >
                  {suite.description}
                </p>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-2" data-testid={`suite-detail-price-${index}`}>
                    <span className="text-2xl font-serif text-[#1A1A1A]">
                      ₦{suite.price_per_night.toLocaleString()}
                    </span>
                    <span className="text-[#3A3A3A]">/night</span>
                  </div>
                  <div className="flex items-center gap-2" data-testid={`suite-detail-guests-${index}`}>
                    <Users size={20} className="text-[#C9A84C]" strokeWidth={1.5} />
                    <span className="text-[#3A3A3A]">Up to {suite.max_guests} guests</span>
                  </div>
                </div>

                {/* Amenities */}
                <div className="mb-8">
                  <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-4">
                    Amenities
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {suite.amenities.map((amenity, amenityIndex) => (
                      <div
                        key={amenityIndex}
                        data-testid={`suite-amenity-${index}-${amenityIndex}`}
                        className="flex items-center gap-2"
                      >
                        <Check size={16} className="text-[#C9A84C]" strokeWidth={2} />
                        <span className="text-sm text-[#3A3A3A]">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  to="/book"
                  state={{ selectedSuite: suite.id }}
                  data-testid={`suite-reserve-btn-${index}`}
                  className="inline-block bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-12 py-4 text-sm tracking-widest uppercase"
                >
                  Reserve This Suite
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Suites;